import React, { Component } from "react";
import Body from "../item/body";
import Image from "../item/image";
import MoreColors from "../item/more-colors";
import { format } from "lib/globalize-util";
import { merge, $ } from "glamor";
import classNames from "classnames";
import Banner from "../item/banner";
import ColorBanner from "../item/color-banner";
import { themeOptions } from "styles/theme";
import ViewBase from "./base";
import { ItemCellSize } from "constants/types";
import { IItemRecord } from "records/item/item";
import type { IScope } from "reducers/scope";
import { CatalogEntryRecord } from "records/catalog/catalog-entry";

const ITEM_HEIGHT = 744;
const ITEM_WIDTH = 330;
const SHADOW = 9;
const MAX_COLOR_CHIPS_COUNT = 72;
const CHIPS_AMOUNT_IN_ROW = 9;

const CONTAINER_HEIGHT = ITEM_HEIGHT + SHADOW * 2;
const CONTAINER_WIDTH = ITEM_WIDTH + SHADOW * 2;

const IMAGE_SIZE = "M";

interface IProps {
    actions: any;
    item: IItemRecord;
    view: { sections: Array<{ Component: any; key: string; title: string; viewDef: string }> };
    scope: IScope;
    catalogEntry: CatalogEntryRecord;
    viewNotes: boolean;
}

interface IState {
    hover: boolean;
    inActiveList: boolean;
    inOrder: boolean;
}

class LargeTileView extends Component<IProps, IState> {
    static height = CONTAINER_HEIGHT;
    static width = CONTAINER_WIDTH;
    static horizontalOffset = 16 - SHADOW * 2;
    static verticalOffset = 16 - SHADOW * 2;

    static styles = merge(
        {
            width: `${CONTAINER_WIDTH}px`,
            height: `${CONTAINER_HEIGHT}px`,
        },
        $(" .item", {
            height: `${ITEM_HEIGHT}px`,
            width: `${ITEM_WIDTH}px`,
            padding: "8px",
        }),
        $(" .imageHolder", {
            position: "relative",
            overflow: "hidden",
            height: "479px",
            width: "100%",
            flexShrink: 0,
            flexGrow: 0,
            display: "flex",
            alignItems: "center",
        }),
        $(" .moreColorLabel", {
            top: "404px",
            left: "8px",
        }),
        $(" .swatches", {
            bottom: "0px",
            left: "8px",
            right: "8px",
        }),
        $(" .swatches.showAllSwatches", {
            top: "0px",
        }),
        $(" .swatches.colorChipsEnabledCollapsed", {
            height: "50px !important",
        }),
        $(" .swatches.colorChipsEnabledOpen", {
            height: "310px !important",
            position: "absolute",
            bottom: "0px !important",
            backgroundColor: "rgba(255,255,255,.95)",
        }),
        $(" .swatchContainer .sliderContainer", {
            width: `${ITEM_WIDTH - 10}px`,
            height: "100%",
        }),
        $(" .disabledBanner", {
            position: "absolute",
            top: "50%",
            left: "0px",
            right: "0px",
            zIndex: 4,
            backgroundColor: "rgba(45, 45, 45, .8)",
            lineHeight: "50px",
            transform: "translateY(-50%)",
            color: "white",
            textAlign: "center",
            fontSize: "16px",
            fontWeight: "normal",
        }),
        $(" .unavailableProduct", {
            position: "absolute",
            width: `${ITEM_WIDTH - 8}px`,
            backgroundColor: themeOptions.palette.errorBackgroundColor,
            color: themeOptions.palette.errorColor,
            height: "514px",
            top: "20%",
            padding: "8px",
            fontSize: "12px",
            left: "4px",
        }),
        $(" .unavailable", {
            opacity: "0.3",
        }),
    );

    declare renderActions: (visibleButtons?: number, isTile?: boolean) => React.ReactElement;
    declare handleItemMoreInfo: (e: React.SyntheticEvent) => void;
    declare renderBulkSelector: () => React.ReactElement;
    declare productActionMenu: () => React.ReactElement;
    declare getShipStart: () => Date;
    declare handleSwatchSelected: (e: Event, swatch: { _id: string; name: string }) => void;

    render() {
        const { actions, item, view, scope, catalogEntry, viewNotes } = this.props;
        const { hover, inOrder, inActiveList } = this.state;
        const { onMoreInfo, onToggleViewMore } = actions;
        const swapOnHover = scope.brand.hasPreference("swap_product_image_on_hover");

        let upperElements;

        if (!viewNotes) {
            upperElements = [
                <ColorBanner
                    key="colorBanner"
                    item={item}
                    hover={hover}
                    inOrder={inOrder}
                    inActiveList={inActiveList}
                />,
                <Banner key="banner" item={item} scope={scope} />,
                this.renderActions(3, false),
            ];

            if (onMoreInfo) {
                upperElements.push(
                    <button
                        key="viewDetails"
                        onClick={this.handleItemMoreInfo}
                        className={classNames("quickview", {
                            quickviewHover: hover,
                        })}
                    >
                        {format("View Detail")}
                    </button>,
                );
            }

            if (item) {
                if (!item.isAvailable(scope)) {
                    upperElements.push(
                        <div key="disabledBanner" className="disabledBanner">
                            {format("Inactive")}
                        </div>,
                    );
                } else if (item._disabled && !scope.brand.hasPreference("hide_currency_warning")) {
                    upperElements.push(
                        <div key="disabledBanner" className="disabledBanner">
                            {format("Not available for |code|", { code: scope.currencyCode })}
                        </div>,
                    );
                }
            }
        }

        return (
            <article>
                {this.renderBulkSelector()}
                {this.productActionMenu()}
                <div className={`imageHolder ${item && item.__out_of_range ? "unavailable" : ""}`}>
                    <Image swapOnHover={swapOnHover} item={item} scope={scope} size={IMAGE_SIZE} />
                    {item && view && upperElements}
                    {this.renderActions(3, false)}
                </div>
                <div className={`content ${item && item.__out_of_range ? "unavailable" : ""}`}>
                    <Body
                        item={item}
                        shipStart={this.getShipStart()}
                        view={view}
                        showPrice={true}
                        size={ItemCellSize.LargeTile}
                        scope={scope}
                    />
                </div>
                {item && !item.__product_customizations_template_id && (
                    <MoreColors
                        item={item}
                        scope={scope}
                        onSwatchSelected={this.handleSwatchSelected}
                        maxChipsAmount={CHIPS_AMOUNT_IN_ROW}
                        chipsCountOnSlide={MAX_COLOR_CHIPS_COUNT}
                        onToggleViewMore={onToggleViewMore}
                    />
                )}
                {item && item.__out_of_range && catalogEntry && (
                    <div className="unavailableProduct">{catalogEntry.unavailableProductWarning(scope)}</div>
                )}
            </article>
        );
    }
}

export default ViewBase(LargeTileView);
